import React from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { withRouter } from 'react-router-dom';
import loadable from '@loadable/component';
import moment from 'moment';

// components, widgets, reusable elements
import ContactSalesPopup from '/src/components/ContactForms/ContactSalesPopup';
import DirectoryExtension from '/src/pages/directories/helpers/DirectoryExtension';
import { DirectorySlidingContent, DirectoryFooter, ExportTable, InvestorDetailRow, InvestorDetailModal, TeamDetailRow, TeamDetailModal, ExportChartsModal, SaveSearchPopup, SavedSearchList, DirTable, TableHeader, SelectableFieldsModal, DataExportPopup, DataExportMessagePopup } from '/src/pages/directories/components';
import { StartupDirectoryExits, StartupDirectoryCharts, StartupDirectoryFunding, StartupDirectoryLandscape } from '/src/pages/directories/views';
import { FilterModal, DirectoryFilters } from '/src/pages/directories/filters';
const SuccessPopup = loadable(() => import(/* webpackChunkName: "SuccessPopup" */ '/src/components/Reusable/SuccessPopup'));

// helpers
import * as filterHelper from '/src/lib/filterHelper';
import * as startupHelper from '/src/pages/directories/helpers/startupDirHelpers';
import { renderProxyAmount } from '/src/pages/directories/helpers/fundingRowHelpers';
import { createRequestGuard } from '/src/pages/directories/helpers/requestGuard';
import { isPageFullySelected } from '/src/pages/directories/helpers/directoryHelpers';
import { checkEmailStatus, getSEOUrl, amountFormatter, formatValuationRange, withBootstrapPlugin } from '/src/lib/helpers';
import { prepareFilterParam } from '/src/lib/queryParams';
import { handleRedirectMessage } from '/src/components/Reusable/toast';

// constants
import { ALLOWED_QUERY_OPERATOR, FILTER_LAYOUT_TYPES, DIRECTORY_STARTUPS, DIRECTORY_INVESTORS, RECORD_COUNT_DEFAULT, HUBSPOT_FORM_SOURCE, YEAR_FOUNDED_START_VALUE } from '/src/lib/constants';
import { WEB_URL } from '/src/lib/endpoints';
import { MAGNITT_ACCESS_ROLES } from '/src/lib/constants/userRoles';
import { COMPONENT_NAME } from '/src/lib/constants';
import { startupDirectorySelectableFields } from '/src/pages/directories/constants/selectableFieldsConfig';

// apis
import taxonomyApi from '/src/apis/taxonomyApi';
import searchApi from "/src/apis/searchApi";
import fundingApi from '/src/apis/fundingApi';

// filter constants
import { startupFilterConfig, startupFundingFilterConfig } from '/src/pages/directories/constants/filterConfigStartup';



const YEAR = parseInt(moment().format('YYYY'));

class Startup extends DirectoryExtension {

    constructor(props) {
        super(props);
        this.entityType = 'STARTUP';
        this.listGuard = createRequestGuard();
        this.countGuard = createRequestGuard();

        const initState = this.initializeStateValues();
        const initTaxoState = this.initializeTaxoStateValues();

        this.state = {

            componentName: COMPONENT_NAME.Startup,

            // initialize state values based on filter schema
            ...initState,
            ...initTaxoState,

            // manage counts
            count: 0,
            limit: this.props.user.is_loggedin ? RECORD_COUNT_DEFAULT.default : RECORD_COUNT_DEFAULT.loggedOut,
            page: 1,
            numoffilter: 0,

            // table data
            data: false,

            filterDefinition: {
                keyword: [{ name: '', type: 'list' }],
                location: [],
                city: [],
                industry: [],
                stage: [],
                type: [],
                technology: [],
                date: [
                    { name: 'title', value: 'Date Founded', type: 'title' },
                    {
                        name: 'date',
                        value: `${YEAR_FOUNDED_START_VALUE}-${YEAR}`,
                        min: YEAR_FOUNDED_START_VALUE,
                        max: YEAR,
                        type: 'range',
                    },
                ],
                employee: [
                    { name: 'title', value: 'Number of employees', type: 'title' },
                    { name: 'employee', value: '0-450', min: 0, max: 450, type: 'range' },
                ],
                active: [
                    { name: 'active', value: 'Active', type: 'title' },
                    { name: 'no', value: 'No', type: 'radio', checked: '0' },
                    { name: 'yes', value: 'Yes', type: 'radio', checked: '0' },
                ],
                fundraising: [
                    { name: 'fundraising', value: 'Are you fundraising?', type: 'title' },
                    { name: 'yes', value: 'Yes', type: 'radio', checked: '0' },
                    { name: 'no', value: 'No', type: 'radio', checked: '0' },
                ],
                totalamount: [
                    { name: 'title', value: 'Total amount raised', type: 'title' },
                    {
                        name: 'total',
                        value: '0-400000000',
                        min: 0,
                        max: 1500000000,
                        type: 'range',
                    },
                ],
                investmentVehicle: []
            },

            // sorting
            sortlist: [
                {
                    name: 'Recently Funded',
                    order: 'desc',
                    field: 'last_date_raised'
                },
                {
                    name: 'Total Funding: High to Low',
                    order: 'desc',
                    field: 'total_fund',
                },
                {
                    name: 'Total Funding: Low to High',
                    order: 'asc',
                    field: 'total_fund',
                },
            ],

            // actions
            isClearAllFilter: false,

            // validation
            popupSuccessMsg: '',

            // seo
            location_seo: '',
            industry_seo: '',
            locations_seo: '',
            industries_seo: '',
            table_title: 'Startups',
            page_title: 'Discover Emerging Venture Markets Startups | MAGNiTT',
            page_description: 'MAGNiTT provides a full list of startups and early-stage companies in the Middle East, Africa, Pakistan and Turkey. Discover startups in different industries and the investments made.',
            page_url: '/startups',
            showSEOSection: false,

            // selectable fields
            isSubscribedUser: false,
            selectableFields: startupDirectorySelectableFields,

            // saved search
            isSearchSaveable: false,
            isSaveSearchDialogVisible: false,
            savedLists: [],
            updateSavedList: false,
            savedSearch: false,

            // data export
            arrSelectedEntities: [],

            // contact sales popup source
            contactSalesPopUpSource: HUBSPOT_FORM_SOURCE.startups_dir_locked_section,

            // directory views
            isTableView: true,
            isLandscapeView: false,
            isChartView: false,
            isFundingView: false,
            isExitsView: false,
            currentTab: 'TableView',
            hasLoadedChartView: false,
            hasLoadedLandscapeView: false,

            // directory views (data)
            directoryCharts: {
                filter: {},
                limit: 10,
                networkMapData: {
                },
                refresh: 0
            },
            directoryNetworkMap: {
                filter: {},
                limit: 9,
                networkMapData: {
                    data: []
                },
                refresh: 0
            },
            directoryFunding: {
                filter: {},
                limit: 9,
                refresh: 0,
                sortList: [
                    { name: 'Date Raised: New to Old', order: 'desc', field: 'date_raised' },
                    { name: 'Date Raised: Old to New', order: 'asc', field: 'date_raised' },
                    { name: 'Amount Raised: High to Low', order: 'desc', field: 'amount' },
                    { name: 'Amount Raised: Low to High', order: 'asc', field: 'amount' },
                    { name: 'Total Funding: High to Low', order: 'desc', field: 'total_amount_raised' },
                    { name: 'Total Funding: Low to High', order: 'asc', field: 'total_amount_raised' }
                ],
                sort: 'Date Raised: New to Old',
            },
            fundingCount: 0,
            fundingTabCount: 0,
            showFundingCount: false,
            directoryExits: {
                filter: {},
                refresh: 0,
                sortList: [
                    { name: 'Acquisition Date: New to Old', order: 'desc', field: 'date_raised' },
                    { name: 'Acquisition Date: Old to New', order: 'asc', field: 'date_raised' },
                    { name: 'Acquired Amount: High to Low', order: 'desc', field: 'amount' },
                    { name: 'Acquired Amount: Low to High', order: 'asc', field: 'amount' },
                    { name: 'Total Funding: High to Low', order: 'desc', field: 'total_amount_raised' },
                    { name: 'Total Funding: Low to High', order: 'asc', field: 'total_amount_raised' }
                ],
                sort: 'Acquisition Date: New to Old',
            },
            exitsCount: 0,
            removeSkeletonTable: false,
        };

        this.handleChange = this.handleChange.bind(this);
        this.handleApplyKeywordFilter = this.handleApplyKeywordFilter.bind(this);
        this.handleKeywordEnter = this.handleKeywordEnter.bind(this);
        this.Login = this.Login.bind(this);
        this.changeOrder = this.changeOrder.bind(this);
        this.changeLimit = this.changeLimit.bind(this);
        this.getSavedSearches = this.getSavedSearches.bind(this);
        this.renderSavedLists = this.renderSavedLists.bind(this);
        this.clearFilter = this.clearFilter.bind(this);
        this.isComponentMounted = false;
    }

    componentDidMount = () => {
        this.isComponentMounted = true;
        handleRedirectMessage(this.props.location);
        this.handleStartupDirectoryMount()
    }

    componentWillUnmount = () => {
        this.isComponentMounted = false;
        this.listGuard.cancel();
        this.countGuard.cancel();
    }

    Login(event) {
        const headerLogin = document.getElementById('headerLogin');
        if (headerLogin) {
            headerLogin.click();
        }
    }

    filterValues = (filter = '') => {

        const {
            page,
            limit,
            data,
            selectableFields,
            totalamountMinSuffix,
            totalamountMaxSuffix,
            amountraisedMinSuffix,
            amountraisedMaxSuffix,
            valuationMinSuffix,
            valuationMaxSuffix,
            exitAmountMinSuffix,
            exitAmountMaxSuffix,
            isTableView,
            isSubscribedUser
        } = this.state;
        const { user } = this.props;

        const { isPageChanged } = filter;
        delete filter.isPageChanged;

        const tempLimit = isSubscribedUser ? limit : RECORD_COUNT_DEFAULT.loggedOut;

        // Update state only when there is change in filters
        if (!isEqual(this.state.directoryCharts.filter, filter)) {
            this.updateState(prevState => ({
                directoryCharts: {
                    ...prevState.directoryCharts,
                    filter: cloneDeep(filter),
                    refresh: prevState.directoryCharts.refresh + 1
                },
                directoryNetworkMap: {
                    ...prevState.directoryNetworkMap,
                    filter: cloneDeep(filter),
                    refresh: prevState.directoryNetworkMap.refresh + 1
                },
                directoryFunding: {
                    ...prevState.directoryFunding,
                    filter: cloneDeep(filter),
                    refresh: prevState.directoryFunding.refresh + 1
                },
                directoryExits: {
                    ...prevState.directoryExits,
                    filter: cloneDeep(filter),
                    refresh: prevState.directoryExits.refresh + 1
                }
            }));
        }

        const { id: requestId, signal } = this.listGuard.start();
        this.updateState({ loadingDirPage: true });

        startupHelper.getStartups(
            page,
            tempLimit,
            isPageChanged,
            filter,
            user,
            startupFilterConfig,
            selectableFields,
            totalamountMinSuffix,
            totalamountMaxSuffix,
            amountraisedMinSuffix,
            amountraisedMaxSuffix,
            valuationMinSuffix,
            valuationMaxSuffix,
            exitAmountMinSuffix,
            exitAmountMaxSuffix,
            signal
        ).then(
            response => {
                if (this.listGuard.isStale(requestId)) return;

                this.mapToState(response);
                this.calculateFiltersCount();
                // keep current selection on page change so rows ticked while loading aren't dropped
                this.updateState({ loadingDirPage: false, ...(isPageChanged ? {} : { arrSelectedEntities: [] }) });

                // we need to close the collapsed table rows whenever the data or sort is updated
                // otherwise they will remain open
                if (typeof window.jQuery !== 'undefined') {
                    $(function () {
                        if (document.getElementsByClassName("table-collapse") && $?.fn?.collapse) {
                            $('.table-collapse').collapse('hide');
                        }
                    })
                }
            }
        ).catch(() => {
            if (this.listGuard.isStale(requestId)) return;
            this.updateState({ loadingDirPage: false });
        });

        // Hide funding count if filters not applicable for funding breakdown
        const showFundingCount = this.checkFundingCountDisplay(filter);
        this.updateState({
            showFundingCount
        });

        // Get funding breakdowns count
        const fundingFilters = startupHelper.prepNewFiltersFunding(
            filter,
            startupFundingFilterConfig,
            totalamountMinSuffix,
            totalamountMaxSuffix,
            amountraisedMinSuffix,
            amountraisedMaxSuffix,
            valuationMinSuffix,
            valuationMaxSuffix,
            exitAmountMinSuffix,
            exitAmountMaxSuffix
        );

        const countRequest = this.countGuard.start();
        fundingApi.getFundingBreakdownsCount(filter, fundingFilters, countRequest.signal).then(response => {
            if (this.countGuard.isStale(countRequest.id)) return;
            if (!isEmpty(response)) {
                this.updateState({
                    fundingCount: response.count
                });
            }
        }).catch((err) => {
            if (this.countGuard.isStale(countRequest.id)) return;
            console.error(err);
        });
    }

    mapToState = (stateObj) => {
        Object.entries(stateObj).forEach(([key, value]) => {
            this.updateState({ [key]: value });
        });
    }

    applySEOFilters = () => {

        let pageTitle, pageDescription
        let isoCode, isoCodePromise
        let industryCode, industryPromise
        let location = ''
        let industry = ''
        let filterParams = this.props.match.params
        let tempFilter = this.state.tempFilter
        let thisObj = this;

        if ('iso' in filterParams) {
            isoCode = filterParams.iso

            isoCodePromise = taxonomyApi.getLocationByISOCode(isoCode).then(
                response => {
                    if (response.length > 0) {
                        tempFilter.location.push({ id: response[0].id, name: response[0].name })
                        location = response[0]
                    }
                }
            )
        }

        if ('industry' in filterParams) {
            industryCode = filterParams.industry
            industryPromise = taxonomyApi.getIndustryByCode(industryCode).then(
                response => {
                    if (response.length > 0) {
                        tempFilter.primary_industry_seo = [];
                        tempFilter.primary_industry_seo.push({ id: response[0].id, name: response[0].name });
                        industry = response[0];
                    }
                }
            )
        }

        // Get MENA countries for SEO section
        let menaCountriesPromise = taxonomyApi.getMENACountries().then(
            response => {
                thisObj.updateState({
                    locations_seo: response,
                });
            }
        )

        // Get top industries for SEO section
        let topIndustriesPromise = taxonomyApi.getTopIndustries().then(
            response => {
                thisObj.updateState({
                    industries_seo: response,
                });
            }
        )

        let filterPromises = [isoCodePromise, industryPromise, menaCountriesPromise, topIndustriesPromise];

        Promise.all(filterPromises).then(() => {
            if (!isEmpty(industry)) {
                pageTitle = `${industry.name} Startups in ${location.name} | MAGNiTT`
                pageDescription = `This list of ${industry.name} startups in ${location.name} provides data on their funding history. Insights about top startups, investments and exits, investors of these companies, their management team, and recent news are also included.`
            } else {
                pageTitle = `Startups in ${location.name} | MAGNiTT`
                pageDescription = `This list of startups in ${location.name} provides data on their funding history. Insights about top startups, investments and exits, investors of these companies, their management team, and recent news are also included.`
            }

            // SEO pages default to Total Funding sort instead of the directory's Recently Funded default
            const updatedFilter = { ...tempFilter, order: { field: 'total_fund', order: 'desc' } };

            this.updateState({
                tempFilter: updatedFilter,
                sort: 'Total Funding: High to Low',
                directoryFunding: { ...this.state.directoryFunding, sort: 'Total Funding: High to Low' },
                directoryExits: { ...this.state.directoryExits, sort: 'Total Funding: High to Low' },
                location_seo: location,
                industry_seo: industry,
                table_title: pageTitle,
                page_title: pageTitle,
                page_description: pageDescription,
                showSEOSection: true
            }, () => {
                this.calculateFiltersCount();
                this.filterValues(this.state.tempFilter);
            })
        });

    }

    updatePageTitle = () => {
        let { tempFilter, count } = this.state
        let tableTitle = 'Startups'
        let pageTitle = 'Discover Emerging Venture Markets Startups'

        if (tempFilter.location.length == 1) {
            tableTitle = pageTitle = `${tableTitle} in ${tempFilter.location[0].name}`
        }

        if (tempFilter.city.length == 1) {
            tableTitle = pageTitle = `${tableTitle} in ${tempFilter.city[0].name}`
        }

        if (tempFilter.primary_industry.length == 1) {
            tableTitle = pageTitle = `${tempFilter.primary_industry[0].name} ${tableTitle}`
        }

        pageTitle = `${pageTitle} - ${count.toLocaleString()} | MAGNiTT`
        this.updateState({ table_title: tableTitle, page_title: pageTitle });
    }

    toggleSaveSearchDialog = (shouldOpen) => {
        if (!checkEmailStatus(this.props.user.email_status)) {
            return
        }

        this.updateState({
            isSaveSearchDialogVisible: shouldOpen,
        });
    };

    onSaveSearch = () => {
        this.getSavedSearches()
        this.updateState({
            isSearchSaveable: false, updateSavedList: false
        });
    };

    setParentState = (state, callback) => {
        this.updateState(
            {
                ...this.state,
                ...state,
            },
            callback
        );
    };

    updateFilters = () => {
        this.filterValues(this.state.tempFilter);
    };

    getDataSort = () => {
        const { tempFilter } = this.state;
        if (isEmpty(tempFilter.order)) {
            return 'last_date_raised^:desc';
        } else {
            return `${tempFilter.order.field}^:${tempFilter.order.order}`;
        }
    }

    // Export goes through Startup.getList, which only understands startup-level filter keys - stay in the Table View's key "dialect"
    getExportFilters = () => {
        const { tempFilter } = this.state;

        const result = this.prepNewFiltersStartup(tempFilter);
        return result;
    }

    renderCountryIndustriesSection = () => {
        let { location_seo, industry_seo, industries_seo } = this.state
        let list = []
        let countryName = location_seo.name
        let isoCode = location_seo.iso_code

        if (industry_seo) {
            let industryId = industry_seo.id
            industries_seo.map(
                industry => {
                    if (industry.id != industryId) {
                        let link = getSEOUrl('startups', isoCode, industry.code)
                        list.push(<li key={"seo_" + industry.id}><a href={link}>{`${industry.name} Startups in ${countryName}`}</a></li>)
                    }
                }
            )
        } else {
            industries_seo.map(
                industry => {
                    let link = getSEOUrl('startups', isoCode, industry.code)
                    list.push(<li key={"seo_" + industry.id}><a href={link}>{`${industry.name} Startups in ${countryName}`}</a></li>)
                }
            )
        }

        return <ul style={{ fontSize: 14 }}>
            <h1>Startups by Industry</h1>
            {list}
        </ul>
    }

    renderIndustryByCountrySection = () => {
        let { location_seo, industry_seo, locations_seo } = this.state
        let list = []

        if (industry_seo) {
            let industryName = industry_seo.name
            let industryCode = industry_seo.code

            locations_seo.map(
                location => {
                    if (location.id != location_seo.id) {
                        let link = getSEOUrl('startups', location.iso_code, industryCode)
                        list.push(<li key={"seo_" + location.id}><a href={link}>{`${industryName} Startups in ${location.name}`}</a></li>)
                    }
                }
            )
        } else {
            locations_seo.map(
                location => {
                    if (location.id != location_seo.id) {
                        let link = getSEOUrl('startups', location.iso_code)
                        list.push(<li key={"seo_" + location.id}><a href={link}>{`Startups in ${location.name}`}</a></li>)
                    }
                }
            )
        }

        return <ul style={{ fontSize: 14 }}>
            <h1 className="mt-3 mt-md-0">Startups by Country</h1>
            {list}
        </ul>
    }

    renderStartups = () => {
        const list = []
        list.push(<li key={"seo_startup"}><h1>Startups</h1></li>)
        DIRECTORY_STARTUPS.forEach((startup, i) => {
            list.push(<li key={"seo_startup_" + i}><a href={startup.link}>{startup.name}</a></li>)
        })
        return list
    }

    renderInvestors = () => {
        const list = []
        list.push(<li key={"seo_inv"}><h1>Investors</h1></li>)
        DIRECTORY_INVESTORS.forEach((startup, i) => {
            list.push(<li key={"seo_inv_" + i}><a href={startup.link}>{startup.name}</a></li>)
        })
        return list
    }

    renderSavedLists = (search) => {
        const searchList = Array.isArray(search) ? search : [];
        searchList.map((search, j) => {
            const currentUrl = window.location.pathname + window.location.search;
            const filtersUrl = filterHelper.convertToUrl(search.filters);

            search.url = `/${search.entity_type}${filtersUrl}&search_id=${search.id}`;
            const decodeUrl = currentUrl.replaceAll('%3E', '>').replaceAll('%3C', '<')
            search.isSelected = (search.url === decodeUrl);
        })
        this.updateState({ savedLists: searchList });
    }

    getSavedSearches = async (page = "", limit = "") => {
        const sort = 'updated_at^:DESC';
        let { user } = this.props;
        let directory = 'startups'

        const filters = prepareFilterParam([{
            key: 'entity_type',
            op: ALLOWED_QUERY_OPERATOR.equal,
            value: [(directory)]
        }])

        const response = await searchApi.getSavedSearch(user.user_id, filters, limit, page, sort)
        this.renderSavedLists(response.records);
        return response;
    }

    showContactForm = () => {
        this.updateState({ contactSalesPopUpSource: HUBSPOT_FORM_SOURCE.startups_dir_locked_section })
        withBootstrapPlugin('modal', ($) => $('#contactFormModal').modal({
            backdrop: 'static', keyboard: true, show: true
        }));
    }

    handleResetSortOrder = (currentTab) => {
        const { tempFilter, directoryFunding, directoryExits, isFundingView, isExitsView, isLandscapeView, isTableView, showSEOSection } = this.state

        if (currentTab === "isFundingView") {
            if (!isFundingView) {
                const updatedFilter = {
                    ...tempFilter,
                    order: showSEOSection
                        ? { field: 'total_amount_raised', order: 'desc' }
                        : { field: 'date_raised', order: 'desc' }
                }
                this.updateState({
                    tempFilter: updatedFilter,
                    sort: directoryFunding.sort,
                })
            }
        } else if (currentTab === "isExitsView") {
            if (!isExitsView) {
                const updatedFilter = {
                    ...tempFilter,
                    order: showSEOSection
                        ? { field: 'total_amount_raised', order: 'desc' }
                        : { field: 'date_raised', order: 'desc' }
                }
                this.updateState({
                    tempFilter: updatedFilter,
                    sort: directoryExits.sort,
                })
            }
        } else if (!isTableView && !isLandscapeView) {
            const updatedFilter = {
                ...tempFilter,
                order: showSEOSection
                    ? { field: 'total_fund', order: 'desc' }
                    : { field: 'last_date_raised', order: 'desc' }
            }
            this.updateState({
                tempFilter: updatedFilter,
                sort: showSEOSection ? 'Total Funding: High to Low' : 'Recently Funded',
            })
        }
    }

    changeTab = (currentTab) => {
        this.handleResetSortOrder(currentTab)
        const previousTab = this.state.currentTab;

        let obj = {};
        obj.isTableView = false;
        obj.isLandscapeView = false
        obj.isChartView = false;
        obj.isFundingView = false;
        obj.isExitsView = false;
        obj[currentTab] = true;
        obj.currentTab = currentTab.replace("is", '');
        if (currentTab === 'isChartView') {
            obj.hasLoadedChartView = true;
        } else if (currentTab === 'isLandscapeView') {
            obj.hasLoadedLandscapeView = true;
        }
        this.updateState(obj, () => {
            this.calculateFiltersCount();
        });

        // Clean up tooltips based on which tab we're leaving
        if (typeof window.jQuery !== 'undefined') {
            $(function () {

                // Only hide chart tooltips when leaving ChartView, show them when entering
                if (previousTab === 'isChartView') {
                    $(".d3-tooltip").css('display', 'none');
                } else if (currentTab === 'isChartView') {
                    $(".d3-tooltip").css('display', 'block');
                }

                // Only hide landscape tooltips when leaving LandscapeView, show them when entering
                if (previousTab === 'isLandscapeView') {
                    $(".popover").css('display', 'none');
                } else if (currentTab === 'isLandscapeView') {
                    $(".popover").css('display', 'block');
                }
            });
        }
    }

    renderTabViews = () => {
        return (
            <div className="btn-group startup-nav-bar"
                role="group"
                aria-label="Button group with nested dropdown">
                <button
                    onClick={() => this.changeTab('isTableView')}
                    type="button"
                    className={`btn btn-sm ${this.state.isTableView ? 'btn-main' : 'btn-main-outline'}`}
                >
                    <i className="fa fa-table"></i>
                    Table View
                </button>
                <button onClick={() => this.changeTab('isFundingView')}
                    type="button"
                    className={`btn btn-sm ${this.state.isFundingView ? 'btn-main' : 'btn-main-outline'}`}
                >
                    <i className="fas fa-funnel-dollar"></i>
                    Funding View
                </button>
                <button onClick={() => this.changeTab('isExitsView')}
                    type="button"
                    className={`btn btn-sm ${this.state.isExitsView ? 'btn-main' : 'btn-main-outline'}`}
                >
                    <i className="fa fa-sign-out-alt"></i>
                    Exits View
                </button>
                <button onClick={() => this.changeTab('isLandscapeView')}
                    type="button"
                    className={`btn  btn-sm ${this.state.isLandscapeView ? 'btn-main' : 'btn-main-outline'}`}
                    id="landscape-view-btn"
                >
                    <i className="fa fa-th-large"></i>
                    Landscape View
                </button>
                <button
                    onClick={() => this.changeTab('isChartView')}
                    type="button"
                    className={`btn btn-sm ${this.state.isChartView ? 'btn-main' : 'btn-main-outline'}`}
                    id="chart-view-btn"
                >
                    <i className="fa fa-chart-line"></i>
                    Chart View
                </button>
            </div>
        );
    }

    // syncSavedSearchUrl is inherited from DirectoryExtension (uses the default
    // prepFiltersForSyncedUrl, which calls this.prepNewFiltersStartup)

    render() {

        const { user } = this.props;
        const {
            arrSelectedEntities,
            isTableView,
            isChartView,
            isLandscapeView,
            isFundingView,
            isExitsView,
            currentTab,
            chartTableData,
            selectedInvestorModal,
            hasLoadedChartView,
            hasLoadedLandscapeView
        } = this.state;
        const isLoggedOut = user && !user.is_loggedin;
        const isSeoDirectory = this?.props?.match?.params?.iso;

        let content = null;
        let tableContent = {};

        tableContent.rows = [];
        tableContent.pinnedRow = [];

        if (this.state.data !== false && this.state.data.length) {

            this.state.data.map((startup, i) => {

                // prepare fields

                // first column
                // logo, name

                let image;
                if (!isEmpty(startup.logo_data) && startup.logo_data.path) {
                    image = encodeURI(startup.logo_data.path);
                    image = <div className='startupLogo' style={{ backgroundImage: `url('${image}')` }}></div>
                } else {
                    image = <div className='startupLogo'><h2>{startup.name.substr(0, 1).toUpperCase()}</h2></div>
                }

                let checkBox = '';
                let nameHeader = 'Name';

                const isStartupChecked = arrSelectedEntities.includes(startup.id);

                const roles = this.props.user.roles;
                if (roles.includes(MAGNITT_ACCESS_ROLES.directory_access) || roles.includes(MAGNITT_ACCESS_ROLES.limited_directory_access)) {
                    // checkbox to select startups to be exported
                    checkBox =
                        <div className="custom-control custom-checkbox">
                            <input type="checkbox" className="custom-control-input" id="select-startup" checked={isStartupChecked} />
                            {/* ND - it won't work without this empty label */}
                            <label htmlFor="select-startup"
                                className="custom-control-label"
                                onClick={(event) => this.handleCheckBox(event, startup.id)}>
                            </label>
                        </div>;

                    // checkbox to select all startups for export
                    nameHeader = <div className='d-flex align-items-end'>
                        <div className="custom-control custom-checkbox">
                            <input type="checkbox" className="custom-control-input" id="select-startup-all" checked={isPageFullySelected(this.state.data, arrSelectedEntities)} />
                            {/* ND - it won't work without this empty label */}
                            <label htmlFor="select-startup-all" className="custom-control-label" onClick={this.handleCheckAllBoxes}></label>
                        </div>
                        <div>Name</div>
                    </div>;
                }

                /*
                    note: expand-slider should only have 1 trigger for each table row
                    (otherwise causes issues w/ next-prev buttons)
                */

                const showFunding = (startup.number_of_rounds_raised > 0 && user.is_loggedin && window.innerWidth > 788);
                const elementId = ("funding-row-" + startup.id);
                const nameRow = (
                    <div className="d-flex align-items-center">
                        {checkBox}
                        <a className="d-flex align-items-top">
                            {image}
                            <div className="flex-column" style={{ marginLeft: 10, marginTop: -4 }}>
                                <div className="startup-name" data-action="expand-slider" data-entityid={startup.id}>
                                    {startup.name.trim()}
                                </div>
                                {showFunding ?
                                    <div className='messageBtn accordion-toggle' data-toggle="collapse" data-target={'#' + elementId} aria-expanded="false" onClick={() => this.getFundingDataForSubRow(startup, this.state.tempFilter.funding_verification_status)}>
                                        <i className="fas fa-angle-down"></i>
                                    </div> : isLoggedOut &&
                                    <div className='messageBtn accordion-toggle' onClick={this.showContactForm}>
                                        <i className="fas fa-lock" /> {' '}  See Funding
                                    </div>
                                }
                            </div>
                        </a>
                    </div>
                );

                // default field
                tableContent.header = [nameHeader]
                let fieldData = [{ name: nameRow }];

                // check selectable fields
                this.state.selectableFields.map((field) => {
                    if (field.checked && field.allowAccess) {

                        const itemField = startup[field.keyName];
                        let tempData = "-";
                        tableContent.header.push(field.fieldName);

                        if (itemField || (itemField === 0 && field.keyName == "is_active")) {
                            switch (field.keyName) {
                            case "business_type_data":
                                if (itemField[0] && itemField[0].name) {
                                    tempData = itemField[0].name;
                                }
                                break;

                            case "sub_industry_data":
                            case "secondary_industry_data":
                            case "technology_data":
                            case "fund_data":
                                if (itemField && itemField.length > 0) {
                                    let arrTemp = [];
                                    itemField.forEach(ind => {
                                        if (ind && ind.name) {
                                            arrTemp.push(ind.name);
                                        }
                                    })
                                    tempData = arrTemp.length > 0 ? arrTemp.join(", ") : "-";
                                }
                                break;

                            case "is_active":
                                tempData = (itemField === 1 ? "Active" : "Inactive");
                                break;

                            case "date_founded":
                            case "last_date_raised":
                                tempData = moment(itemField).format("MMM YYYY");
                                break;

                            case "last_stage_data":
                                // tempData = itemField.name
                                //     ? (itemField.is_proxy
                                //         ? <span className="amount--estimated">{itemField.name}</span>
                                //         : itemField.name)
                                //     : "-";
                                tempData = itemField?.name || "Undisclosed";
                                break;

                            case "primary_industry_data":
                            case "hq_data":
                            case "development_stage_data":
                            case "city_data":
                                tempData = itemField.name ? itemField.name : "-";
                                break;

                            case "investors_data":
                                if (!isEmpty(itemField)) {
                                    tempData = (
                                        <InvestorDetailRow
                                            investedInName={startup.name}
                                            investorData={itemField}
                                            setState={(s, c) => { this.updateState(s, c) }}
                                            investedInId={startup.id}
                                            investedInType={this.entityType.toLowerCase()}
                                            userHasDirectoryAccess={this.state.isSubscribedUser}
                                            selectedFundingStageName={false}
                                        />
                                    )
                                }
                                break;

                            case "last_amount_raised":
                                if (!isEmpty(itemField) && itemField.amount) {
                                    tempData = <span className={itemField.has_proxy ? 'amount--estimated' : ''} title={itemField.has_proxy ? 'Estimated amount' : undefined}>
                                        {amountFormatter(parseInt(itemField.amount, 10))}
                                    </span>;
                                } else {
                                    tempData = "-";
                                }
                                break;

                            case "website":
                            case "description":
                            case "email_relations":
                            case "number_of_rounds_raised":
                                tempData = itemField;
                                break;

                            case "fund_raised_data":
                                // paid rows blurred / not available
                                if (!this.state.isSubscribedUser) {
                                    tempData = "paidData";
                                } else if (!isEmpty(itemField) && itemField.amount) {
                                    tempData = <span className={itemField.has_proxy ? 'amount--estimated' : ''} title={itemField.has_proxy ? 'Estimated amount' : undefined}>
                                        {amountFormatter(parseInt(itemField.amount, 10))}
                                    </span>;
                                } else {
                                    tempData = "Undisclosed";
                                }
                                break;

                            case "valuation":
                                if (!isEmpty(itemField) && (itemField && itemField.min && itemField.max)) {
                                    tempData = (
                                        <span className={itemField.is_estimate ? 'amount--estimated' : ''}>
                                            {formatValuationRange(itemField.min, itemField.max)}
                                        </span>
                                    )
                                } else {
                                    tempData = "-"
                                }

                                break;

                            case "team_data":
                                if (!isEmpty(itemField)) {
                                    tempData = (
                                        <TeamDetailRow
                                            entityName={startup.name}
                                            teamData={itemField}
                                            setState={(s, c) => { this.updateState(s, c) }}
                                        />
                                    )
                                }
                                break;

                            default:
                                tempData = "-";
                            }
                        }

                        fieldData.push({ [field.keyName]: tempData });

                    }
                })

                // required for funding sub row functionality
                fieldData.push({ id: startup.id, code: startup.code, element_name: startup.name });

                tableContent.rows.push(fieldData);

            })

            content = (
                <DirTable
                    content={tableContent}
                    user={this.props.user}
                    entityType={this.entityType}
                    showFundingSubRow={true}
                    state={this.state}
                    filterValues={this.filterValues}
                    tempFilter={this.state.tempFilter}
                    setState={(s, c) => { this.updateState(s, c) }}
                />
            )

        }

        let { isSearchSaveable, isSaveSearchDialogVisible, table_title } = this.state;

        let tableHeader = <TableHeader
            // header
            type="startup"
            count={this.state.count}
            fundingCount={this.state.fundingCount}
            fundingTabCount={this.state.fundingTabCount}
            showFundingCount={this.state.showFundingCount}
            exitsCount={this.state.exitsCount}
            title={table_title}
            limit={this.state.limit}
            page={this.state.page}
            // sort by
            selectedItem={this.state.sort}
            sortList={this.state.isFundingView ? this.state.directoryFunding.sortList
                : (this.state.isExitsView ? this.state.directoryExits.sortList : this.state.sortlist)}
            changeOrder={this.changeOrder}
            // data request
            isShowExportBtn={(arrSelectedEntities.length > 0) || (this.state.numoffilter > 0)}
            getFilters={() =>
                this.prepNewFiltersStartup(this.state.tempFilter)
            }
            user={this.props.user}
            entityType={this.entityType}
            Login={this.Login}
            // saved search
            isSearchSaveable={isSearchSaveable}
            toggleSaveSearchDialog={() =>
                this.toggleSaveSearchDialog(true)
            }
            savedLists={this.state.savedLists}
            getSavedSearches={this.getSavedSearches}
            // selectable fields
            selectableFields={true}
            isSubscribedUser={this.state.isSubscribedUser}
            renderTabViews={this.renderTabViews}
            isTableView={this.state.isTableView}
            isLandscapeView={this.state.isLandscapeView}
            isChartView={this.state.isChartView}
            isFundingView={this.state.isFundingView}
            isExitsView={this.state.isExitsView}
            // for chart export
            setParentState={(s, c) => { this.updateState(s, c) }}
            showContactForm={this.showContactForm}
            isSeoDirectory={isSeoDirectory}
        />

        // todo
        // we can pass this/state directly instead of doing it in an ugly way
        const filterProps = {
            passedState: this.state,
            user: this.props.user,
            directory: 'startups',
            syncSavedSearchUrl: this.syncSavedSearchUrl,
            filterLayoutType: FILTER_LAYOUT_TYPES.startups,
            location: this.state.location,
            locations: this.state.locations,
            tempFilter: this.state.tempFilter,
            handleRemoveFilter: this.handleRemoveFilter,
            handleSelectFilter: this.handleSelectFilter,
            handleChangeInput: this.handleChangeInput,
            handleKeywordEnter: this.handleKeywordEnter,
            filterValues: this.filterValues,
            handleClearAllFilters: this.handleClearAllFilters,
            keyword: this.state.keyword,
            investor: this.state.investor,
            acquirer: this.state.acquirer,
            onKeywordChange: this.onKeywordChange,
            handleApplyKeywordFilter: this.handleApplyKeywordFilter,
            // development stage filter
            stage: this.state.stage,
            stages: this.state.stages,
            clearFilter: this.clearFilter,
            numoffilter: this.state.numoffilter,
            setParentState: this.setParentState,
            handleChange: this.handleChange,
            totalamountMinSuffix: this.state.totalamountMinSuffix,
            totalamountMaxSuffix: this.state.totalamountMaxSuffix,
            amountraisedMinSuffix: this.state.amountraisedMinSuffix,
            amountraisedMaxSuffix: this.state.amountraisedMaxSuffix,
            valuationMinSuffix: this.state.valuationMinSuffix,
            valuationMaxSuffix: this.state.valuationMaxSuffix,
            exitAmountMinSuffix: this.state.exitAmountMinSuffix,
            exitAmountMaxSuffix: this.state.exitAmountMaxSuffix,
            isSearchSaveable: isSearchSaveable,
            applyFilters: this.handleSearchFilters,
            toggleSaveSearchDialog: this.toggleSaveSearchDialog,
            filterDefinition: this.state.filterDefinition,
            gender: this.state.gender,
            handleItemSelect: this.handleItemSelect,
            handleChangeText: this.handleChangeText,
            updateFilters: this.updateFilters,
            calculateFiltersCount: this.calculateFiltersCount,
            isLoggedIn: this.props.user.is_loggedin,
            Login: this.Login,
            // region filter
            region: this.state.region,
            regionsList: this.state.regions,
            getRegionLocations: this.getRegionLocations,
            // industry filters
            industriesList: this.state.primary_industrys,
            // view indicators
            isTableView: this.state.isTableView,
            isChartView: this.state.isChartView,
            isLandscapeView: this.state.isLandscapeView,
            isFundingView: this.state.isFundingView,
            isExitsView: this.state.isExitsView,
            // city filter
            city: this.state.city,
            // investment vehicle filter
            investmentVehicles: this.state.investmentVehicles,
            handleMultiSelectFilter: this.handleMultiSelectFilter,
            // technology filter
            technology: this.state.technology,
            technologys: this.state.technologys,
            // fund filter
            fund: this.state.fund,
            funds: this.state.funds,
            // investor filters
            investorRegion: this.state.investorRegion,
            investorRegions: this.state.investorRegions,
            investorLocation: this.state.investorLocation,
            investorLocations: this.state.investorLocations,
            investorTaxonomyType: this.state.investorTaxonomyType,
            investorTaxonomyTypes: this.state.investorTaxonomyTypes,
            // acquirer filters
            acquirerRegion: this.state.acquirerRegion,
            acquirerRegions: this.state.acquirerRegions,
            acquirerLocation: this.state.acquirerLocation,
            acquirerLocations: this.state.acquirerLocations,
            acquirerType: this.state.acquirerType,
            acquirerTypes: this.state.acquirerTypes,
            // company
            company: this.state.company,
            // investment filters
            investmentRegion: this.state.investmentRegion,
            investmentRegions: this.state.investmentRegions,
            investmentLocation: this.state.investmentLocation,
            investmentLocations: this.state.investmentLocations,
            // seo check
            isSeoDirectory
        }

        return (
            <div className="footer-margin-offset">
                <Helmet
                    title={this.state.page_title}
                    link={[
                        {
                            rel: 'canonical',
                            href: `${WEB_URL}${this.state.page_url}`,
                        },
                    ]}
                    meta={[
                        {
                            name: 'description',
                            content: this.state.page_description,
                        },
                        {
                            name: 'abstract',
                            content: this.state.page_description,
                        },
                        {
                            property: 'og:type',
                            content: 'article',
                        },
                        {
                            property: 'og:url',
                            content: `${WEB_URL}${this.state.page_url}`,
                        },
                        {
                            property: 'og:title',
                            content: this.state.page_title,
                        },
                    ]}
                    script={[
                        {
                            type: 'application/ld+json',
                            innerHTML: `{
                                "@context": "http://schema.org",
                                "@type": "BreadcrumbList",
                                "itemListElement": [{
                                    "@type": "ListItem",
                                    "position": 1,
                                    "name": "startups",
                                    "item": "${WEB_URL}/startups"
                                }]
                            }`,
                        },
                    ]}
                />

                {isSaveSearchDialogVisible && (
                    <SaveSearchPopup
                        close={() => {
                            this.toggleSaveSearchDialog(false);
                        }}
                        user={this.props.user}
                        filters={this.prepNewFiltersStartup(this.state.tempFilter)}
                        pageName='startups'
                        onSave={this.onSaveSearch}
                        setParentState={(s, c) => { this.updateState(s, c) }}
                    />
                )}

                <section className='directory content-box'>
                    <div className='row'>

                        <div className="column-full rightside">

                            {/* table header */}
                            {tableHeader}

                            <DirectoryFilters
                                {...filterProps}
                                currentTab={currentTab}
                                showLegend={isExitsView || isFundingView || isTableView}

                            />

                            <div className={`${isTableView ? "" : "d-none"} table-row-container`}>
                                {content}
                                <DirectoryFooter
                                    // data
                                    count={this.state.count}
                                    data={this.state.data}
                                    loadingDirPage={this.state.loadingDirPage}
                                    allowNavigateWhileLoading
                                    user={this.props.user}
                                    page={this.state.page}
                                    limit={this.state.limit}
                                    tempFilter={this.state.tempFilter}
                                    filterValues={this.filterValues}
                                    // func
                                    clearFilter={this.clearFilter}
                                    setState={(s, c) => { this.updateState(s, c) }}
                                    removeSkeletonTable={this.state.removeSkeletonTable}
                                    tabIsVisible={isTableView}
                                />
                            </div>

                            {hasLoadedChartView && (
                                <div className={`${isChartView ? "" : "d-none"}`}>
                                    <StartupDirectoryCharts
                                        data={this.state.directoryCharts}
                                        totalamountMinSuffix={this.state.totalamountMinSuffix}
                                        totalamountMaxSuffix={this.state.totalamountMaxSuffix}
                                        amountraisedMinSuffix={this.state.amountraisedMinSuffix}
                                        amountraisedMaxSuffix={this.state.amountraisedMaxSuffix}
                                        valuationMinSuffix={this.state.valuationMinSuffix}
                                        valuationMaxSuffix={this.state.valuationMaxSuffix}
                                        exitAmountMinSuffix={this.state.exitAmountMinSuffix}
                                        exitAmountMaxSuffix={this.state.exitAmountMaxSuffix}
                                        clearFilter={this.clearFilter}
                                        tempFilter={this.state.tempFilter}
                                        user={this.props.user}
                                        setState={(s, c) => { this.updateState(s, c) }}
                                        tabIsVisible={isChartView}
                                        isSavedSearch={this.state.savedSearch}
                                    />
                                </div>
                            )}

                            {hasLoadedLandscapeView && (
                                <div className={`${isLandscapeView ? "" : "d-none"}`}>
                                    <StartupDirectoryLandscape
                                        data={this.state.directoryNetworkMap}
                                        totalamountMinSuffix={this.state.totalamountMinSuffix}
                                        totalamountMaxSuffix={this.state.totalamountMaxSuffix}
                                        amountraisedMinSuffix={this.state.amountraisedMinSuffix}
                                        amountraisedMaxSuffix={this.state.amountraisedMaxSuffix}
                                        valuationMinSuffix={this.state.valuationMinSuffix}
                                        valuationMaxSuffix={this.state.valuationMaxSuffix}
                                        exitAmountMinSuffix={this.state.exitAmountMinSuffix}
                                        exitAmountMaxSuffix={this.state.exitAmountMaxSuffix}
                                        tempFilter={this.state.tempFilter}
                                        clearFilter={this.clearFilter}
                                        user={this.props.user}
                                        setState={(s, c) => { this.updateState(s, c) }}
                                        amplitudeTrack={this.amplitudeTrack}
                                        tabIsVisible={isLandscapeView}
                                    />
                                </div>
                            )}

                            <div className={`${isFundingView ? "" : "d-none"}`}>
                                <StartupDirectoryFunding
                                    fundingData={this.state.directoryFunding}
                                    entityType={this.entityType}
                                    tempFilter={this.state.tempFilter}
                                    clearFilter={this.clearFilter}
                                    filterValues={this.filterValues}
                                    totalAmountMinSuffix={this.state.totalamountMinSuffix}
                                    totalAmountMaxSuffix={this.state.totalamountMaxSuffix}
                                    amountraisedMinSuffix={this.state.amountraisedMinSuffix}
                                    amountraisedMaxSuffix={this.state.amountraisedMaxSuffix}
                                    exitAmountMinSuffix={this.state.exitAmountMinSuffix}
                                    exitAmountMaxSuffix={this.state.exitAmountMaxSuffix}
                                    user={this.props.user}
                                    setState={(s, c) => { this.updateState(s, c) }}
                                    isFundingView={isFundingView}
                                />
                            </div>

                            <div className={`${isExitsView ? "" : "d-none"}`}>
                                <StartupDirectoryExits
                                    exitsData={this.state.directoryExits}
                                    entityType={this.entityType}
                                    tempFilter={this.state.tempFilter}
                                    clearFilter={this.clearFilter}
                                    filterValues={this.filterValues}
                                    totalAmountMinSuffix={this.state.totalamountMinSuffix}
                                    totalAmountMaxSuffix={this.state.totalamountMaxSuffix}
                                    amountraisedMinSuffix={this.state.amountraisedMinSuffix}
                                    amountraisedMaxSuffix={this.state.amountraisedMaxSuffix}
                                    exitAmountMinSuffix={this.state.exitAmountMinSuffix}
                                    exitAmountMaxSuffix={this.state.exitAmountMaxSuffix}
                                    user={this.props.user}
                                    setState={(s, c) => { this.updateState(s, c) }}
                                    isExitsView={isExitsView}
                                />
                            </div>

                            {this.state.showSEOSection &&
                                <section className="seo_container">
                                    <div className="row">
                                        <div className="col-12 col-md-8">
                                            {this.renderCountryIndustriesSection()}
                                        </div>
                                        <div className="col-12 col-md-4">
                                            {this.renderIndustryByCountrySection()}
                                        </div>
                                    </div>
                                    <div style={{ paddingTop: 20 }}>
                                        {isEmpty(this.state.industries_seo) ?
                                            <p>This list of startups in {this.state.location_seo.name} provides data on their funding history. Insights about top startups, investments and exits,  investors of these companies, their management team, and recent news are also included.</p>
                                            :
                                            <p>This list of startups in {this.state.location_seo.name} provides data on the trends in the {this.state.industry_seo.name} industry. Insights about top startups, investments and exits,  investors of these companies, their management team, and recent news are also included.</p>
                                        }
                                    </div>
                                </section>
                                ||
                                <div className={`directory-footer  ${isSeoDirectory ? "directory-seo-container" : ""}`}>
                                    <div className="directory-footer__left seo_container">
                                        <div className="directory-footer__startups">
                                            {this.renderStartups()}
                                        </div>
                                        <div className="directory-footer__investors">
                                            {this.renderInvestors()}
                                        </div>
                                    </div>
                                </div>
                            }

                            {/*
                              * the table element that's used to display startup data for pdf/ppt export
                              * the element isn't displayed directly on the page, but is used when generating the export
                            **/}
                            {chartTableData &&
                                <ExportTable content={chartTableData} />
                            }

                        </div>

                    </div>

                    {isTableView &&
                        <DirectorySlidingContent
                            // data
                            entityData={this.state.data}
                            entityType={this.entityType}
                            user={this.props.user}
                            // functions
                            setState={(s, c) => { this.updateState(s, c) }}
                        />
                    }

                </section>

                <SuccessPopup handleClose={() => { }} msg={this.state.popupSuccessMsg} />

                <DataExportPopup
                    dataSetType="STARTUP"
                    directory="Startup"
                    user={this.props.user}
                    isSubscribedUser={this.state.isSubscribedUser}
                    selectableFields={[...this.state.selectableFields]}
                    recordsCount={this.state.count}
                    sort={this.state.sort}
                    sortList={this.state.sortlist}
                    selectedEntities={this.state.arrSelectedEntities}
                    // functions
                    getFilters={() => this.getExportFilters()}
                    setState={(s, c) => { this.updateState(s, c) }}
                    getSort={() => this.getDataSort()}
                    showSuccessPopup={this.showSuccessPopup}
                />

                <DataExportMessagePopup />

                <SelectableFieldsModal
                    type="startup"
                    user={this.props.user}
                    isSubscribedUser={this.state.isSubscribedUser}
                    selectableFields={this.state.selectableFields}
                    setState={(s, c) => { this.updateState(s, c) }}
                    updateResults={() => this.filterValues(this.state.tempFilter)}
                />

                <SavedSearchList
                    updateSavedList={this.state.updateSavedList}
                    user={this.props.user}
                    getSavedSearches={this.getSavedSearches}
                />

                {/* note: checking isFundingView/isExitsView to avoid conflicts between the 2 InvestorDetailModals */}
                {/* (funding view/exits view + table view) - both render their own #investors-modal */}
                {!isFundingView && !isExitsView &&
                    <InvestorDetailModal
                        investedInName={this.state.investedInName}
                        investedInId={this.state.investedInId}
                        investorData={selectedInvestorModal}
                        userHasDirectoryAccess={this.state.isSubscribedUser}
                        selectedFundingStageName={false}
                        isFundingView={false}
                    />
                }

                <TeamDetailModal
                    entityName={this.state.selectedTeamEntity}
                    teamData={this.state.selectedTeamData}
                />

                {/* used for rendering popup when user clicks unlock in the team member contact details
                    DirectorySlider component - team section */}
                <ContactSalesPopup page={this.state.contactSalesPopUpSource} />

                <FilterModal {...filterProps} currentTab={currentTab} />

                {/* for chart pdf ppt export */}
                <ExportChartsModal
                    chartExportData={this.state.chartExportData}
                    ischartExporting={this.state.ischartExporting}
                    previewError={this.state.previewError}
                    filterString={this.state.filterString} // used for chart-export-log
                    user={this.props.user}
                />

            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    user: state.userReducer,
});

export default connect(mapStateToProps)(withRouter(Startup));
